/**
 * `JourneyStrip` — "How AI agents moved through your site this week".
 *
 * Shows DISTINCT AI agents at each step (NOT percentages).
 *   Find        — ADR-0219 — distinct AI agents that fetched a discovery
 *                 file (agent card, llms.txt, UCP, MCP card, ARD catalog,
 *                 /agent gateway). Only measurable via the server-side
 *                 install; without a live Worker/plugin shows "—" with a
 *                 "needs Worker or plugin" caption.
 *   Read        — distinct AI agents with page loads (overview.totals.agents).
 *   Understand  — AI agents / sessions that reached products, collection
 *                 or search (overview.funnel.product + search).
 *   Act         — skill uses (actions) with approved count.
 *
 * When the ranker's top step blocks reading (crawler.blocked), the
 * Understand node gets the coral hollow ring and the preceding
 * connector is dashed.
 */

import type { AgentOverviewResponse } from '@sill/types/api';

import { useC } from '../../lib/theme.js';
import { Tooltip } from '../../components/atoms/Tooltip.js';
import { monoFont, sansFont, serifFont } from './fonts.js';
import { Dash, Kicker } from './MonoLink.js';
import type { SetView } from '../../app/view-router.js';

export interface JourneyStripProps {
  readonly overview: AgentOverviewResponse | null;
  readonly understandBlocked: boolean;
  /** ADR-0219 — Find is only measurable via the Worker/plugin. False
   *  renders the Find node as "—" with a "needs Worker or plugin" caption. */
  readonly serverSideLive: boolean;
  readonly setView: SetView;
  /** 2026-10-02 — click handler for a strip node. The readiness page
   *  opens a drill-down drawer on the readiness view itself rather
   *  than jumping to Agents, so a merchant sees per-step detail in
   *  context. The `setView` prop is kept so the drawer footer can
   *  still deep-link to the Agents view. */
  readonly onNodeClick?: (step: 'find' | 'read' | 'understand' | 'act') => void;
}

interface Node {
  readonly key: 'find' | 'read' | 'understand' | 'act';
  readonly label: string;
  readonly value: number | null; // null = not measured
  readonly caption: string;
  /** Tooltip text — set for "Find" to explain why it is "—". */
  readonly title?: string | undefined;
  /** Where this node navigates. `null` means not clickable. */
  readonly navigate?: (() => void) | null;
}

export function JourneyStrip({ overview, understandBlocked, serverSideLive, setView, onNodeClick }: JourneyStripProps): JSX.Element {
  const C = useC();

  const read = overview?.totals.agents ?? null;
  const understand_sessions = overview === null
    ? null
    : overview.funnel.product + overview.funnel.search;
  const act_actions = overview?.totals.actions ?? null;
  const act_approved = overview?.totals.approved ?? null;

  // ADR-0219 — Find reads from overview.funnel.find_agents when the
  // server-side layer is live. Without a Worker/plugin we have no data
  // source for discovery hits, so show "—" with an actionable caption.
  const find_measurable = serverSideLive && overview !== null;
  const find_value = find_measurable ? overview.funnel.find_agents : null;
  const find_caption = find_measurable
    ? 'fetched your agent card, llms.txt or other discovery files'
    : 'Needs the Cloudflare Worker or WordPress plugin';
  const find_title = find_measurable
    ? undefined
    : 'Find is counted by the Cloudflare Worker or WordPress plugin — the embed script cannot see server-side fetches.';

  // Default navigations fall through to the Agents / Mandates views
  // (previous behaviour, preserved for callers that don't pass
  // `onNodeClick`). When `onNodeClick` IS provided — the readiness
  // page does, so a merchant sees the drill-down drawer in context —
  // every node routes through it instead.
  const navigateFind = onNodeClick !== undefined
    ? () => onNodeClick('find')
    : (find_measurable ? () => setView('agents', { agent_kind: 'ai', agents_mode: 'agents' }) : null);
  const navigateRead = onNodeClick !== undefined
    ? () => onNodeClick('read')
    : () => setView('agents', { agent_kind: 'ai', agents_mode: 'agents' });
  const navigateUnderstand = onNodeClick !== undefined
    ? () => onNodeClick('understand')
    : () => setView('agents', { agent_kind: 'ai', agents_mode: 'demand' });
  const navigateAct = onNodeClick !== undefined
    ? () => onNodeClick('act')
    : () => setView('mandates');
  const nodes: readonly Node[] = [
    {
      key: 'find',
      label: 'Find',
      value: find_value,
      caption: find_caption,
      title: find_title,
      navigate: navigateFind,
    },
    {
      key: 'read',
      label: 'Read',
      value: read,
      caption: 'loaded your pages',
      navigate: navigateRead,
    },
    {
      key: 'understand',
      label: 'Understand',
      value: understand_sessions,
      caption: understandBlocked
        ? 'reached products or search. Most were turned away.'
        : 'reached products or search',
      navigate: navigateUnderstand,
    },
    {
      key: 'act',
      label: 'Act',
      value: act_actions,
      caption: act_actions === null
        ? 'used a skill'
        : act_actions === 0
          ? 'used a skill'
          : `used a skill: ${act_actions.toLocaleString('en-US')} request${act_actions === 1 ? '' : 's'}, ${act_approved?.toLocaleString('en-US') ?? '—'} approved`,
      navigate: navigateAct,
    },
  ];

  return (
    <section
      aria-label="How AI agents moved through your site this week"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        padding: '26px 32px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        fontFamily: sansFont,
      }}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div style={{ fontSize: 15, color: C.text, fontWeight: 500 }}>
          How AI agents moved through your site this week
        </div>
        <div style={{ fontSize: 12, color: C.muted }}>Distinct AI agents at each step</div>
      </div>

      {/* Horizontal on desktop, vertical on mobile. */}
      <div
        className="grid grid-cols-1 md:grid-cols-4"
        style={{ position: 'relative', gap: 20 }}
      >
        {nodes.map((n, i) => {
          const isUnderstand = n.key === 'understand';
          const dotStyle = isUnderstand && understandBlocked
            ? { width: 7, height: 7, borderRadius: '50%', border: `1.5px solid ${C.coral}`, background: C.bg }
            : { width: 9, height: 9, borderRadius: '50%', background: n.value === null ? C.dim : C.text };
          const inner = (
            <>
              <span style={dotStyle} aria-hidden="true" />
              <div><Kicker>{n.label}</Kicker></div>
              <div style={{ fontFamily: serifFont, fontSize: 34, color: C.text, lineHeight: 1 }}>
                {n.value === null ? <Dash /> : n.value.toLocaleString('en-US')}
              </div>
              <div style={{ fontSize: 13, color: isUnderstand && understandBlocked ? C.body : C.muted }}>
                {n.caption}
              </div>
            </>
          );
          const commonStyle = {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: 10,
            alignItems: 'flex-start' as const,
            textAlign: 'left' as const,
            background: 'transparent',
            border: 'none',
            padding: 0,
            fontFamily: sansFont,
            cursor: n.navigate !== null && n.navigate !== undefined ? 'pointer' : 'default',
            color: 'inherit',
          };
          const node = n.navigate !== null && n.navigate !== undefined ? (
            <button
              type="button"
              className="relative"
              onClick={n.navigate}
              aria-label={`${n.label}: ${n.caption}`}
              style={commonStyle}
            >
              {inner}
            </button>
          ) : (
            <div
              className="relative"
              style={commonStyle}
            >
              {inner}
            </div>
          );
          return (
            <div key={n.key} style={{ position: 'relative' }}>
              {n.title !== undefined && n.title !== null && n.title !== '' ? (
                <Tooltip content={n.title} displayMode="block">{node}</Tooltip>
              ) : node}
              {i < nodes.length - 1 ? (
                <span
                  className="hidden md:block"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 4,
                    left: 17,
                    right: -12,
                    height: 1,
                    borderTop: isUnderstand && understandBlocked ? `1px dashed ${C.dim}` : `1px solid ${C.border}`,
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </div>
      <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
        Counts are distinct AI agents, not people.
      </div>
    </section>
  );
}
